import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { ScissorsIcon, SparklesIcon, SyringeIcon, HeartPulseIcon, EyeIcon, SmileIcon } from 'lucide-react';
import { Language } from '../App';
interface ServicesProps {
  language: Language;
}
const content = {
  en: {
    title: 'Surgical & Aesthetic Services',
    subtitle: 'Comprehensive plastic surgery and aesthetic procedures tailored to your goals.',
    services: [{
      icon: ScissorsIcon,
      title: 'Liposuction',
      description: 'Body contouring and fat removal for a sculpted, natural-looking result.'
    }, {
      icon: SparklesIcon,
      title: 'Tummy Tuck',
      description: 'Abdominoplasty to tighten and reshape the abdominal area.'
    }, {
      icon: HeartPulseIcon,
      title: 'Breast Surgery',
      description: 'Augmentation, reduction, and lift procedures customized to you.'
    }, {
      icon: EyeIcon,
      title: 'Facelift & Eyelid Surgery',
      description: 'Facial rejuvenation procedures for a refreshed, youthful appearance.'
    }, {
      icon: SyringeIcon,
      title: 'Injectables & Fillers',
      description: 'Non-surgical treatments including Botox and dermal fillers.'
    }, {
      icon: SmileIcon,
      title: 'Body Contouring',
      description: 'Comprehensive body sculpting including Brazilian Butt Lift (BBL).'
    }]
  },
  es: {
    title: 'Servicios Quirúrgicos y Estéticos',
    subtitle: 'Cirugía plástica integral y procedimientos estéticos adaptados a sus objetivos.',
    services: [{
      icon: ScissorsIcon,
      title: 'Liposucción',
      description: 'Contorno corporal y eliminación de grasa para un resultado natural y esculpido.'
    }, {
      icon: SparklesIcon,
      title: 'Abdominoplastia',
      description: 'Cirugía para tensar y remodelar el área abdominal.'
    }, {
      icon: HeartPulseIcon,
      title: 'Cirugía de Senos',
      description: 'Aumento, reducción y levantamiento personalizados para usted.'
    }, {
      icon: EyeIcon,
      title: 'Lifting Facial y Blefaroplastia',
      description: 'Procedimientos de rejuvenecimiento facial para una apariencia fresca y juvenil.'
    }, {
      icon: SyringeIcon,
      title: 'Inyectables y Rellenos',
      description: 'Tratamientos no quirúrgicos incluyendo Botox y rellenos dérmicos.'
    }, {
      icon: SmileIcon,
      title: 'Contorno Corporal',
      description: 'Escultura corporal integral incluyendo Levantamiento de Glúteos (BBL).'
    }]
  }
};
export function Services({
  language
}: ServicesProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const t = content[language];
  return <section ref={ref} className="py-24 px-4 sm:px-6 lg:px-8 bg-espresso" data-id="element-145" data-mp="2uqcaisctby3e">
      <div className="max-w-7xl mx-auto" data-id="element-146" data-mp="2zpx6ctworqbw">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="text-center mb-16" data-id="element-147" data-mp="2zovw2rnhv7v8">
          <h2 className="text-4xl sm:text-5xl font-bold text-pearl mb-4" data-id="element-148" data-mp="2gsnnpez6qgvp">
            {t.title}
          </h2>
          <p className="text-lg text-pearl/70 max-w-2xl mx-auto" data-id="element-149" data-mp="2lpw8lnbpwz0n">
            {t.subtitle}
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-150" data-mp="2pq6bgr47r9ba">
          {t.services.map((service, index) => {
          const Icon = service.icon;
          return <motion.div key={service.title} initial={{
            opacity: 0,
            y: 30
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} className="bg-brown-dark/60 border border-pearl/10 rounded-2xl p-6 hover:border-pearl/30 transition-colors" data-id="element-151" data-mp="50gxshf8xtk6" data-mpi={index}>
                <div className="bg-pearl/10 rounded-full p-3 w-12 h-12 flex items-center justify-center mb-4" data-id="element-152" data-mp="hitc7v3qfyh7">
                  <Icon className="w-6 h-6 text-pearl" data-id="element-153" data-mp="rah39uwdndi5" />
                </div>
                <h3 className="text-xl font-bold text-pearl mb-2" data-id="element-154" data-mp="rcjwsowqnorm">
                  {service.title}
                </h3>
                <p className="text-pearl/60 text-sm leading-relaxed" data-id="element-155" data-mp="118wrnq40s8ny">
                  {service.description}
                </p>
              </motion.div>;
        })}
        </div>
      </div>
    </section>;
}